"use client";
import { GroupLabelContext } from "./group-label-context.js";
import { createElement, createHook, forwardRef, useId, useSafeLayoutEffect } from "@ariakit/react-utils";
import { removeUndefinedValues } from "@ariakit/utils";
import { useContext } from "react";
//#region src/group/group-label.tsx
const TagName = "div";
/**
* Returns props to create a `GroupLabel` component. This hook must be used in a
* component that's wrapped with `Group` so the `aria-labelledby` prop is
* properly set on the group element.
* @see https://ariakit.com/components/group
* @example
* ```jsx
* // This component must be wrapped with Group
* const props = useGroupLabel();
* <Role {...props}>Label</Role>
* ```
*/
const useGroupLabel = createHook(function useGroupLabel(props) {
	const setLabelId = useContext(GroupLabelContext);
	const id = useId(props.id);
	useSafeLayoutEffect(() => {
		setLabelId?.(id);
		return () => setLabelId?.(void 0);
	}, [setLabelId, id]);
	props = {
		"aria-hidden": true,
		...props,
		id
	};
	return removeUndefinedValues(props);
});
/**
* Renders a label in a group. This component should be wrapped with a
* [`Group`](https://ariakit.com/reference/group) so the `aria-labelledby`
* prop is correctly set on the group element.
* @see https://ariakit.com/components/group
* @example
* ```jsx
* <Group>
*   <GroupLabel>Label</GroupLabel>
* </Group>
* ```
*/
const GroupLabel = forwardRef(function GroupLabel(props) {
	return createElement(TagName, useGroupLabel(props));
});
//#endregion
export { GroupLabel, useGroupLabel };

//# sourceMappingURL=group-label.js.map